import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Quote } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { IMAGES, MARQUEE_ITEMS, SERVICES, PROJECTS, TESTIMONIALS, STATS } from "@/lib/data";

const MANIFESTO = [
  {
    num: "01",
    title: "Savoir-faire",
    text: "Créée en 1993 par Pierre-Yves Le Roch, notre SARL compte 12 compagnons couvreurs qui travaillent l'ardoise, le zinc et les membranes avec la précision des artisans d'art.",
  },
  {
    num: "02",
    title: "Protection",
    text: "Toutes nos prestations sont garanties par notre assureur AXA, en responsabilité civile et décennale. Votre sérénité est contractuelle.",
  },
  {
    num: "03",
    title: "Proximité",
    text: "Un interlocuteur à votre écoute du premier appel à la réception du chantier, des délais respectés — partout dans le Morbihan et les Côtes d'Armor.",
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 160]);
  const heroOpacity = useTransform(scrollY, [0, 550], [1, 0.25]);

  return (
    <main data-testid="home-page">
      <Seo
        title="Couverture Le Roch — Couvreur à Pontivy (Morbihan)"
        siteName="LE ROCH"
        description="Couvreur à Pontivy depuis 1993 : couverture ardoise & zinc, étanchéité, isolation, bardage et zinguerie dans le Morbihan et les Côtes d'Armor. Devis gratuit."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Couverture Le Roch",
          description: "Entreprise de couverture, étanchéité, isolation et zinguerie à Pontivy — SARL créée en 1993, certifiée RGE, assurances AXA.",
          telephone: "+33297380540",
          email: "contact@leroch-couverture.fr",
          foundingDate: "1993",
          address: {
            "@type": "PostalAddress",
            streetAddress: "ZA Kerponner Villeneuve Postic",
            postalCode: "56300",
            addressLocality: "Pontivy",
            addressCountry: "FR",
          },
          areaServed: ["Morbihan", "Côtes d'Armor"],
        }}
      />

      {/* HERO */}
      <section className="grain relative flex min-h-screen items-center overflow-hidden bg-roch-navy">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Toiture architecturale moderne au coucher du soleil"
            className="h-[115%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-roch-navy/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-roch-navy via-roch-navy/20 to-roch-navy/60" />
        </motion.div>

        <div className="relative mx-auto w-full max-w-7xl px-4 pb-28 pt-40 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-roch-orange"
          >
            LE ROCH — Couverture · Étanchéité
          </motion.p>

          <h1 className="mt-8 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <MaskLines
              delay={0.35}
              lines={[
                <>DES TOITS QUI</>,
                <>BRAVENT</>,
                <span className="text-roch-orange">LE TEMPS.</span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
          >
            Couvreurs à Pontivy depuis 1993. Ardoise, zinc, étanchéité,
            isolation : 14 compagnons au service du Morbihan et des Côtes d'Armor.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/devis"
              data-testid="hero-devis-cta-button"
              className="group inline-flex items-center gap-3 rounded-full bg-roch-orange px-8 py-4 font-display text-base font-bold text-roch-navy transition-colors duration-300 hover:bg-roch-orangeHover"
            >
              Demander un devis gratuit
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-button"
              className="inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-display text-base font-semibold text-white transition-colors duration-300 hover:border-white/60 hover:bg-white/5"
            >
              Nous contacter
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-16 grid max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 backdrop-blur-sm sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.label} className="bg-roch-navy/60 px-5 py-5">
                <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* MANIFESTO */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-roch-orange">Notre manifeste</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-roch-navy sm:text-4xl lg:text-5xl">
              Trois engagements. Aucun compromis.
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.12} className="border-t-2 border-roch-navy/10 pt-8">
                <p className="font-display text-6xl font-extrabold text-roch-orange/90">{m.num}</p>
                <h3 className="mt-5 font-display text-2xl font-bold text-roch-navy">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{m.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-roch-orange">Nos expertises</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-roch-navy sm:text-4xl lg:text-5xl">
                  Quatre métiers,<br />une exigence.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="group inline-flex items-center gap-2 font-display text-base font-bold text-roch-navy"
              >
                Tous nos services
                <ArrowUpRight className="h-5 w-5 text-roch-orange transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group block overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:border-roch-orange/50 hover:shadow-[0_24px_60px_-20px_rgba(251,195,65,0.35)]"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={s.image}
                      alt={`${s.title} — ${s.tag}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute left-5 top-5 rounded-full bg-roch-navy/85 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur">
                      {s.num} — {s.tag}
                    </span>
                  </div>
                  <div className="p-7 sm:p-8">
                    <h3 className="font-display text-2xl font-bold text-roch-navy">{s.title}</h3>
                    <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                      {s.features.slice(0, 4).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-roch-orange" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE STRIP */}
      <section className="grain relative overflow-hidden bg-roch-navy py-24 sm:py-32">
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/3 lg:block">
          <img src={IMAGES.harness} alt="Couvreur au travail sur une toiture" loading="lazy" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-roch-navy to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Quote className="h-10 w-10 text-roch-orange" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl font-display text-3xl font-extrabold leading-[1.15] tracking-tight text-white sm:text-4xl lg:text-5xl">
              « Nous choisir, c'est choisir la qualité,<br />
              <span className="text-roch-orange">le conseil et des délais respectés. »</span>
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-white/45">
              Pierre-Yves Le Roch — Gérant
            </p>
          </Reveal>
        </div>
      </section>

      {/* REALISATIONS PREVIEW */}
      <section className="py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-roch-orange">Réalisations</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-roch-navy sm:text-4xl lg:text-5xl">
                  Des chantiers qui parlent<br />d'eux-mêmes.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/realisations"
                data-testid="home-realisations-link"
                className="group inline-flex items-center gap-2 font-display text-base font-bold text-roch-navy"
              >
                Voir la galerie
                <ArrowUpRight className="h-5 w-5 text-roch-orange transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3 lg:gap-8">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to="/realisations" className="group block" data-testid={`home-project-${p.id}`}>
                  <div className="relative overflow-hidden rounded-3xl">
                    <img
                      src={p.image}
                      alt={`${p.title} — ${p.work}`}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-roch-navy/90 via-roch-navy/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-roch-orange">{p.location}</p>
                      <h3 className="mt-2 font-display text-xl font-bold text-white">{p.title}</h3>
                      <p className="mt-1 text-sm text-white/60">{p.work}</p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-roch-orange">Ils nous font confiance</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-roch-navy sm:text-4xl lg:text-5xl">
              La parole à ceux<br />qui nous font confiance.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure className="flex h-full flex-col rounded-3xl border border-slate-200 bg-roch-mist p-8" data-testid={`testimonial-${i}`}>
                  <Quote className="h-6 w-6 text-roch-orange" />
                  <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-slate-700 sm:text-base">
                    « {t.quote} »
                  </blockquote>
                  <figcaption className="mt-6 border-t border-slate-200 pt-5">
                    <p className="font-display text-base font-bold text-roch-navy">{t.name}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
